import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { whatsappApi } from '../../../services/whatsappApi';
import { teamService } from '../../verve-teams/services/teamService';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../../../services/api';

const SOCKET_URL = API_BASE_URL;

export const useWhatsApp = () => {
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Modals state
  const [isCreating, setIsCreating] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '', templateName: '' });
  const [templates, setTemplates] = useState([]);

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');

  const { user } = useAuthStore();
  const socketRef = useRef();

  useEffect(() => {
    fetchConversations();
    fetchTemplates();

    socketRef.current = io(SOCKET_URL, {
      withCredentials: true,
    });

    socketRef.current.on('connect', () => {
      console.log('Connected to WhatsApp socket');
    });

    socketRef.current.on('whatsapp:newMessage', (data) => {
      const { conversation, message } = data;

      setConversations(prev => {
        const exists = prev.find(c => c._id === conversation._id);
        if (exists) {
          const filtered = prev.filter(c => c._id !== conversation._id);
          return [conversation, ...filtered];
        } else {
          return [conversation, ...prev];
        }
      });

      setMessages(prev => {
        const convMessages = prev[conversation._id] || [];
        return {
          ...prev,
          [conversation._id]: [...convMessages, message]
        };
      });

      if (message.sender === 'customer' && activeConversationId !== conversation._id) {
        toast.success(`New message from ${conversation.customerName || conversation.customerPhone}`);
      }
    });

    socketRef.current.on('whatsapp:conversationUpdated', (updatedConv) => {
      setConversations(prev => {
        const exists = prev.find(c => c._id === updatedConv._id);
        if (exists) {
          return prev.map(c => c._id === updatedConv._id ? updatedConv : c);
        } else {
          const isOwnerOrAdmin = ['owner', 'admin', 'superadmin'].includes((user?.role || '').toLowerCase());
          const isAssigned = Array.isArray(updatedConv.assignedTo) && updatedConv.assignedTo.some(u => u._id === user?.id || u === user?.id);
          if (isOwnerOrAdmin || isAssigned) {
            return [updatedConv, ...prev];
          }
          return prev;
        }
      });
    });

    socketRef.current.on('whatsapp:messageStatus', ({ messageId, status }) => {
      setMessages(prev => {
        const next = { ...prev };
        for (const [convId, convMsgs] of Object.entries(next)) {
          const updatedMsgs = convMsgs.map(msg =>
            (msg.messageSid === messageId || msg._id === messageId)
              ? { ...msg, status }
              : msg
          );
          next[convId] = updatedMsgs;
        }
        return next;
      });
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [user, activeConversationId]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const res = await whatsappApi.getConversations();
      if (res.success) {
        setConversations(res.data);
      }
    } catch (error) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await whatsappApi.getTemplates();
      if (res.success) {
        setTemplates(res.data);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadMessages = async (conversationId) => {
    if (!messages[conversationId]) {
      try {
        const res = await whatsappApi.getMessages(conversationId);
        if (res.success) {
          setMessages(prev => ({ ...prev, [conversationId]: res.data }));
        }
      } catch (error) {
        toast.error('Failed to load messages');
      }
    }
    setActiveConversationId(conversationId);

    setConversations(prev => prev.map(c =>
      c._id === conversationId ? { ...c, unreadCount: 0 } : c
    ));
  };

  const handleSendMessage = async (content, file = null) => {
    if (!activeConversationId || (!content.trim() && !file)) return;
    try {
      if (file) {
        const formData = new FormData();
        formData.append('conversationId', activeConversationId);
        formData.append('content', content);
        formData.append('file', file);
        await whatsappApi.sendMessage(formData);
      } else {
        await whatsappApi.sendMessage({ conversationId: activeConversationId, content });
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!activeConversationId) return;

    setIsAssigning(true);
    try {
      const res = await whatsappApi.assignConversation(activeConversationId, selectedAssignees);
      if (res.success) {
        toast.success('Conversation assigned successfully');
        setIsAssignModalOpen(false);
      }
    } catch (error) {
      toast.error('Failed to assign conversation');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUpdateStatus = async (conversationId, status) => {
    try {
      const res = await whatsappApi.updateConversationStatus(conversationId, status);
      if (res.success) {
        toast.success(`Deal ${status === 'resolved' ? 'Closed' : 'Reopened'}`);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openAssignModal = async () => {
    try {
      setEmployeeSearchQuery('');
      const res = await teamService.getEmployees({ limit: 1000, status: 'Active' });
      setEmployees(res?.data || []);
      const activeConv = conversations.find(c => c._id === activeConversationId);
      if (activeConv && Array.isArray(activeConv.assignedTo)) {
        setSelectedAssignees(activeConv.assignedTo.map(u => u._id));
      } else {
        setSelectedAssignees([]);
      }
      setIsAssignModalOpen(true);
    } catch (error) {
      toast.error('Failed to load employees');
    }
  };

  const handleToggleAssignee = (employeeId) => {
    setSelectedAssignees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleCreateConversation = async (e) => {
    e.preventDefault();
    if (!newContact.phone) return toast.error('Phone number is required');

    setIsCreating(true);
    try {
      const res = await whatsappApi.createConversation({
        customerName: newContact.name,
        customerPhone: newContact.phone
      });
      if (res.success) {
        toast.success('Conversation created');
        
        const newConv = res.data;
        setConversations(prev => [newConv, ...prev.filter(c => c._id !== newConv._id)]);
        
        // If a template was selected, send it
        if (newContact.templateName) {
           try {
             const templateRes = await whatsappApi.sendTemplate({
                conversationId: newConv._id,
                templateName: newContact.templateName,
                templateLanguage: 'en_US'
             });
             if (templateRes.success) {
                toast.success('Template sent!');
             }
           } catch (templateError) {
             toast.error(templateError?.response?.data?.message || 'Failed to send template');
           }
        }

        setIsCreateModalOpen(false);
        setNewContact({ name: '', phone: '', templateName: '' });
        loadMessages(newConv._id);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create conversation');
    } finally {
      setIsCreating(false);
    }
  };

  const activeConversation = conversations.find(c => c._id === activeConversationId);
  const activeMessages = activeConversationId ? (messages[activeConversationId] || []) : [];

  return {
    conversations,
    activeConversationId,
    setActiveConversationId,
    activeConversation,
    activeMessages,
    loading,
    user,
    
    isCreating,
    isCreateModalOpen,
    setIsCreateModalOpen,
    newContact,
    setNewContact,
    handleCreateConversation,
    templates,

    isAssignModalOpen,
    setIsAssignModalOpen,
    employees,
    selectedAssignees,
    isAssigning,
    employeeSearchQuery,
    setEmployeeSearchQuery,
    openAssignModal,
    handleToggleAssignee,
    handleAssign,

    loadMessages,
    handleSendMessage,
    handleUpdateStatus
  };
};
